<template>
<v-container>
  <!-- Loading State -->
  <v-skeleton-loader
    v-if="loading"
    type="article, card, card"
    class="mt-6"
  ></v-skeleton-loader>
 
  <div v-if="!loading">
    <ValidationObserver v-slot="{handleSubmit}">
      <v-form @submit.prevent="handleSubmit(saveCustomer)">

        <!-- Banner Relbase prefill (creación) -->
        <v-alert
          v-if="relbasePrefilled"
          type="info"
          dense
          outlined
          dismissible
          class="mb-4"
          @input="relbasePrefilled = false"
          border="left"
          colored-border
          color="#35BDC8"
          icon="mdi-check-circle"
        >
          <strong>RUT encontrado en Relbase.</strong> Los datos fiscales fueron completados automáticamente y están bloqueados para mantener consistencia con Relbase.
        </v-alert>

        <!-- Banner edición — cliente sincronizado (AC 9) -->
        <v-alert
          v-if="relbaseEnabled && customer.id && customer.relbase_customer_id"
          type="warning"
          dense
          outlined
          class="mb-4"
          border="left"
          colored-border
          color="#35BDC8"
          icon="mdi-lock"
        >
          Los datos fiscales están sincronizados con Relbase. Para modificarlos, actualícelos en Relbase y presione <strong>Re-sincronizar</strong>.
        </v-alert>

        <!-- Header Section -->
        <v-row class="mb-6 align-center">
          <v-col cols="12" md="7">
            <h1 class="text-h4 font-weight-bold d-flex align-center">
              <v-icon large color="primary" class="mr-3">
                {{customer.id ? 'fas fa-user-edit' : 'fas fa-user-plus'}}
              </v-icon>
              {{customer.id ? 'Actualizar Cliente' : 'Crear Nuevo Cliente'}}
            </h1>
            <div class="text-caption grey--text text--darken-1 mt-1">
              {{customer.id ? `Editando información de ${customer.name}` : 'Ingrese los datos básicos para registrar un nuevo cliente'}}
            </div>
          </v-col>
          <v-col cols="12" md="5" class="d-flex justify-end align-center">
            <v-chip
              v-if="relbaseEnabled && customer.id && customer.relbase_customer_id"
              color="#35BDC8"
              dark
              small
              label
              class="mr-4 font-weight-bold"
            >
              <v-icon small left>mdi-check-circle</v-icon>
              Sincronizado con Relbase
            </v-chip>

            <v-btn
              v-if="relbaseEnabled && customer.id && !customer.relbase_customer_id"
              outlined
              small
              color="#35BDC8"
              class="mr-4"
              :loading="syncLoading"
              @click="syncWithRelbase(false)"
            >
              <v-icon left small>mdi-sync</v-icon>
              Sincronizar con Relbase
            </v-btn>

            <v-btn
              v-if="relbaseEnabled && customer.id && customer.relbase_customer_id"
              outlined
              x-small
              color="grey"
              class="mr-4"
              :loading="syncLoading"
              @click="syncWithRelbase(true)"
              title="Re-sincronizar con Relbase"
            >
              <v-icon x-small>mdi-sync</v-icon>
            </v-btn>

            <v-switch
              class="mt-0 pt-0 mr-6"
              inset
              v-model="customer.status"
              :color="customer.status ? 'success' : 'error'"
              :label="customer.status ? 'ACTIVO' : 'INACTIVO'"
              :disabled="!canUpdateMainInformation"
              hide-details
            ></v-switch>

            <v-btn :loading="saveLoading" type="submit" color="primary" depressed class="px-6 mr-2">
              <v-icon left small>fas fa-save</v-icon>
              Guardar
            </v-btn>

            <v-btn @click="$router.push('/customers')" outlined depressed color="grey darken-2" fab small>
              <v-icon x-small>fas fa-times</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- General Information Card -->
        <v-card outlined class="modern-card mb-8">
          <v-sheet color="grey lighten-5" class="px-6 py-4 border-bottom">
            <div class="text-overline grey--text text--darken-2">Información de la Entidad</div>
          </v-sheet>
          
          <v-card-text class="pa-8 pt-10">
            <v-row>
              <v-col cols="12" md="4">
                <ValidationProvider name="RUT" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="customer.rut"
                    label="RUC / RUT"
                    outlined
                    dense
                    placeholder="Ej: 76.543.210-9"
                    persistent-placeholder
                    :error-messages="errors"
                    :disabled="!canUpdateMainInformation || rutLookupLoading || fiscalFieldsReadOnly"
                    :loading="rutLookupLoading"
                    @blur="onRutBlur"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              
              <v-col cols="12" md="8">
                <ValidationProvider name="Razón Social" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="customer.name"
                    label="Razón Social"
                    outlined
                    dense
                    placeholder="Nombre completo legal de la empresa"
                    persistent-placeholder
                    :error-messages="errors"
                    :disabled="!canUpdateMainInformation || fiscalFieldsReadOnly"
                    :hint="fiscalFieldsReadOnly ? 'Sincronizado con Relbase — solo editable desde Relbase' : ''"
                    persistent-hint
                  ></v-text-field>
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="6">
                <ValidationProvider name="Fantasía" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="customer.fantasy"
                    label="Nombre de Fantasía"
                    outlined
                    dense
                    placeholder="Marca o nombre comercial"
                    persistent-placeholder
                    :error-messages="errors"
                    :disabled="!canUpdateMainInformation || fiscalFieldsReadOnly"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="6">
                <ValidationProvider name="Giro" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="customer.giro"
                    label="Giro o Actividad Económica"
                    outlined
                    dense
                    placeholder="Descripción del giro principal"
                    persistent-placeholder
                    :error-messages="errors"
                    :disabled="!canUpdateMainInformation || fiscalFieldsReadOnly"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>

              <!-- Categorization Row -->
              <v-col cols="12" md="3">
                <tipo-selection 
                  v-model="customer.tipo"
                  :filtro="false"
                  :disabled="!canUpdateMainInformation"
                  label="Tipo de Cliente"
                />
              </v-col>

              <v-col cols="12" md="3">
                <industry-selection 
                  v-model="customer.industry"
                  :filtro="false"
                  :disabled="!canUpdateMainInformation"
                  label="Industria"
                />
              </v-col>

              <v-col cols="12" md="3">
                <rubro-selection 
                  v-model="customer.rubro"
                  :filtro="false"
                  :disabled="!canUpdateMainInformation"
                  label="Rubro"
                />
              </v-col>

              <v-col cols="12" md="3">
                <ValidationProvider name="Vendedor" rules="required" v-slot="{ errors }">
                  <seller-selection 
                    v-model="customer.seller"
                    :disabled="!canUpdateMainInformation"
                    :errors="errors"
                    label="Ejecutivo Asignado"
                  />
                </ValidationProvider>
              </v-col>

              <!-- Financial Row -->
              <v-col cols="12" md="4">
                <ValidationProvider name="Teléfono" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="customer.phone"
                    label="Teléfono Principal"
                    outlined
                    dense
                    placeholder="+56 9 ..."
                    persistent-placeholder
                    :error-messages="errors"
                    :disabled="!canUpdateMainInformation"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="4">
                <ValidationProvider name="Condición de Pago" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="customer.payment"
                    label="Condición de Pago (Días)"
                    type="number"
                    min="0"
                    outlined
                    dense
                    placeholder="Plazo en días"
                    persistent-placeholder
                    suffix="días"
                    :error-messages="errors"
                    :disabled="!canUpdateMainInformation"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="4">
                <ValidationProvider name="Comisión" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="customer.com_agencia"
                    label="Comisión de Agencia"
                    type="number"
                    min="0"
                    outlined
                    dense
                    placeholder="%"
                    persistent-placeholder
                    suffix="%"
                    :error-messages="errors"
                    :disabled="!canUpdateMainInformation"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      <!-- Inline Address + Contact (solo en creación) -->
      <div v-if="!customer.id">
        <v-row>
          <!-- Dirección inline -->
          <v-col cols="12" lg="6">
            <v-card outlined class="modern-card overflow-hidden mb-6">
              <v-sheet color="grey lighten-5" class="px-6 py-4 border-bottom">
                <div class="text-overline grey--text text--darken-2">Dirección Principal</div>
              </v-sheet>
              <v-card-text class="pa-6">
                <ValidationObserver>
                  <address-relbase-inline
                    v-if="relbaseEnabled"
                    v-model="newAddress"
                    :readonly="relbaseLookupFound"
                  />
                  <address-form-inline
                    v-else
                    v-model="newAddress"
                  />
                </ValidationObserver>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Contacto inline -->
          <v-col cols="12" lg="6">
            <v-card outlined class="modern-card overflow-hidden mb-6">
              <v-sheet color="grey lighten-5" class="px-6 py-4 border-bottom">
                <div class="text-overline grey--text text--darken-2">Contacto Principal</div>
              </v-sheet>
              <v-card-text class="pa-6">
                <contact-form-inline
                  v-model="newContact"
                  :readonly="relbaseLookupFound"
                  :readonly-email="relbaseLookupFound && relbaseEmailFound"
                  :readonly-phone="relbaseLookupFound && relbasePhoneFound"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

      </v-form>
    </ValidationObserver>

    <!-- Sub-modules Section -->
    <v-row v-if="customer.id">
      <v-col cols="12" lg="6">
        <!-- Relbase mode: principal contact card -->
        <v-card v-if="relbaseEnabled" outlined class="modern-card overflow-hidden mb-4">
          <v-sheet color="grey lighten-5" class="px-6 py-4 border-bottom d-flex align-center">
            <div>
              <div class="text-overline grey--text text--darken-2">Contacto Principal</div>
              <div class="caption grey--text">Contacto sincronizado con Relbase (máximo 1)</div>
            </div>
          </v-sheet>
          <v-card-text class="pa-6">
            <contact-relbase v-model="customer.contacts" :customerId="customer.id" @updated="refreshCustomer"/>
          </v-card-text>
        </v-card>

        <!-- Relbase mode: additional contacts card -->
        <v-card v-if="relbaseEnabled" outlined class="modern-card overflow-hidden">
          <v-sheet color="grey lighten-5" class="px-6 py-4 border-bottom d-flex align-center">
            <div>
              <div class="text-overline grey--text text--darken-2">Contactos Adicionales</div>
              <div class="caption grey--text">Otros contactos del cliente</div>
            </div>
          </v-sheet>
          <v-card-text class="pa-0">
            <contact-list v-model="customer.contacts" :customerId="customer.id" :skip-first="true"/>
          </v-card-text>
        </v-card>

        <!-- Non-Relbase mode: standard full contact list -->
        <v-card v-else outlined class="modern-card overflow-hidden h-100">
          <v-sheet color="grey lighten-5" class="px-6 py-4 border-bottom d-flex align-center">
            <div>
              <div class="text-overline grey--text text--darken-2">Contactos</div>
              <div class="caption grey--text">Personal clave y encargados</div>
            </div>
          </v-sheet>
          <v-card-text class="pa-0">
            <contact-list v-model="customer.contacts" :customerId="customer.id"/>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card outlined class="modern-card overflow-hidden h-100">
          <v-sheet color="grey lighten-5" class="px-6 py-4 border-bottom d-flex align-center">
            <div>
              <div class="text-overline grey--text text--darken-2">Direcciones</div>
              <div class="caption grey--text">{{ relbaseEnabled ? 'Dirección principal para Relbase' : 'Sucursales y puntos de despacho' }}</div>
            </div>
          </v-sheet>
          <v-card-text class="pa-6">
            <address-relbase v-if="relbaseEnabled" v-model="customer.addresses" :customerId="customer.id" :readonly="fiscalFieldsReadOnly" @updated="refreshCustomer"/>
            <address-list v-else v-model="customer.addresses" :customerId="customer.id"/>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>

  <!-- Dialogs -->
  <confirm 
    v-model="showConfirm" 
    message="El RUT ingresado ya existe. ¿Desea crear una nueva sucursal para este cliente?" 
    @ok="()=>{
      if(customer.id) modifyCustomer()
      else addNewCustomer()
      }"
    @cancel="()=>{customer.branch_office=0; showConfirm=false}"
  />
</v-container>
</template>

<script>
import SellerSelection from '@/components/customers/SellerSelection'
import TipoSelection from '@/components/customers/TipoSelection.vue'
import IndustrySelection from '@/components/customers/IndustrySelection.vue'
import ContactList from '@/components/customers/ContactList.vue'
import AddressList from '@/components/customers/AddressList.vue'
import ContactRelbase from '@/components/relbase/ContactRelbase.vue'
import AddressRelbase from '@/components/relbase/AddressRelbase.vue'
import ContactFormInline from '@/components/relbase/ContactFormInline.vue'
import AddressFormInline from '@/components/relbase/AddressFormInline.vue'
import AddressRelbaseInline from '@/components/relbase/AddressRelbaseInline.vue'
import RubroSelection from '@/components/customers/RubroSelection.vue'
import { getCustomer, createCustomer, updateCustomer, fetchCustomers } from '@/api/customers'
import { syncCustomerRelbase, lookupCustomerByRUT } from '@/api/relbase'
import notifications from '@/utils/notifications'

export default {
  name: 'CreateupdateCustomer',
  components: {
    ContactList,
    AddressList,
    ContactRelbase,
    AddressRelbase,
    ContactFormInline,
    AddressFormInline,
    AddressRelbaseInline,
    SellerSelection,
    TipoSelection,
    IndustrySelection,
    RubroSelection
  },
  data: () => ({
    customer: {
      addresses: [],
      contacts: [],
      seller: '',
      status: true,
      branch_office: 0,
      tipo: '',
      industry: '',
      rubro: '',
    },
    newAddress: { name: 'Principal', region: '', city: '', comune: '', street: '', number: '', suite: '', relbase_city_id: null, relbase_commune_id: null },
    newContact: { first_name: '', last_name: '', email: '', phone: '', cargo: '' },
    saveLoading: false,
    loading: false,
    showConfirm: false,
    syncLoading: false,
    rutLookupLoading: false,
    relbasePrefilled: false,
    relbaseLookupFound: false,
    relbaseEmailFound: false,
    relbasePhoneFound: false,
  }),
  
  async created() {
    if (this.$route.params.customerId) {
      this.loading = true
      await getCustomer(this.$route.params.customerId)
      .then((res) => {
        this.customer = res
        this.customer.seller = this.customer.seller ? this.customer.seller : ''
        this.customer.tipo = this.customer.tipo ? this.customer.tipo : ''
        this.customer.industry = this.customer.industry ? this.customer.industry.id : ''
        this.customer.rubro = this.customer.rubro ? this.customer.rubro.id : ''
      })
      .catch((err) => {
        notifications.showError('Error cargando datos del cliente: ' + err)
      })
      .finally(() => {
        this.loading = false
      })
    }
  },
  methods: {
    async saveCustomer() {
      // Nota: rutValido siempre retorna true por versión PERU según código original
      if (this.customer.id) {
        this.modifyCustomer()
        return
      }
      
      // Verifica si el rut ya existe
      this.saveLoading = true
      await fetchCustomers('', 1, '', '', '', '', this.customer.rut)
      .then((res) => {
        if (res.count > 0) {
          this.customer.branch_office = res.count + 1
          this.showConfirm = true
          this.saveLoading = false
        } else {
          this.addNewCustomer()
        }
      })
      .catch(() => {
        notifications.showError('Error verificando existencia del cliente')
        this.saveLoading = false
      })
    },
    async modifyCustomer() {
      this.saveLoading = true
      await updateCustomer(this.customer)
      .then(() => {
        notifications.showSuccess('Cliente ' + this.customer.name + ' actualizado correctamente')
        this.$router.push('/customers')
      })
      .catch(() => {
        notifications.showError('Error actualizando los datos del cliente')
      })
      .finally(() => {
        this.saveLoading = false
      })
    },
    async addNewCustomer() {
      this.saveLoading = true
      const hasAddress = this.newAddress.street || this.newAddress.city
      const hasContact = this.newContact.first_name || this.newContact.email
      const payload = { ...this.customer }
      if (hasAddress) payload.address_data = { ...this.newAddress }
      if (hasContact) payload.contact_data = { ...this.newContact }
      await createCustomer(payload)
      .then((customer) => {
        // AC 13: sync exitoso
        const syncStatus = customer.sync_result && customer.sync_result.status
        if (syncStatus === 'created' || syncStatus === 'linked') {
          notifications.showSuccess('Cliente creado y sincronizado con Relbase')
        } else if (syncStatus === 'error') {
          // AC 14: sync fallido pero cliente guardado
          notifications.showSuccess('Cliente guardado. No se pudo sincronizar con Relbase — puede sincronizar manualmente después.')
        } else {
          notifications.showSuccess('Cliente registrado exitosamente')
        }
        // AC 2: redirigir al listado (no al formulario de edición)
        this.$router.push('/customers')
      })
      .catch(() => {
        notifications.showError('Error al crear el nuevo cliente')
      })
      .finally(() => {
        this.saveLoading = false
      })
    },
    async onRutBlur() {
      if (this.customer.id) return // solo en creación
      if (!this.relbaseEnabled) return
      if (this.rutLookupLoading) return // evitar requests duplicados
      const rut = (this.customer.rut || '').trim()
      if (!rut) return
      this.rutLookupLoading = true
      try {
        const result = await lookupCustomerByRUT(rut)
        if (result.found) {
          if (result.name) this.customer.name = result.name
          if (result.name_fantasy) this.customer.fantasy = result.name_fantasy
          if (result.business_activity) this.customer.giro = result.business_activity
          if (result.phone) this.customer.phone = result.phone
          // Prefill dirección desde Relbase
          this.newAddress = {
            ...this.newAddress,
            city: result.city || '',
            comune: result.commune || '',
            street: result.address || '',
            relbase_city_id: result.relbase_city_id || null,
            relbase_commune_id: result.relbase_commune_id || null,
          }
          // Prefill contacto desde Relbase normalizando email y teléfono
          const emailValue = (Array.isArray(result.email) ? result.email[0] : result.email) || ''
          const phoneValue = result.phone || ''
          this.newContact = {
            ...this.newContact,
            email: emailValue,
            phone: phoneValue
          }
          this.relbaseEmailFound = !!emailValue
          this.relbasePhoneFound = !!phoneValue
          this.relbasePrefilled = true
          this.relbaseLookupFound = true
        }
      } finally {
        this.rutLookupLoading = false
      }
    },
    async refreshCustomer() {
      if (!this.customer.id) return
      try {
        const res = await getCustomer(this.customer.id)
        this.customer.contacts = res.contacts || []
        this.customer.addresses = res.addresses || []
      } catch (e) {
        // silent refresh
      }
    },
    async syncWithRelbase(force) {
      this.syncLoading = true
      await syncCustomerRelbase(this.customer.id, force)
      .then(async (res) => {
        if (res.status === 'linked') {
          // Relbase match found — backend populated local data, reload full customer
          const fresh = await getCustomer(this.customer.id)
          this.customer.contacts = fresh.contacts || []
          this.customer.addresses = fresh.addresses || []
          this.customer.name = fresh.name
          this.customer.fantasy = fresh.fantasy
          this.customer.giro = fresh.giro
          this.customer.phone = fresh.phone
          this.customer.tipo = fresh.tipo || this.customer.tipo
          this.customer.relbase_customer_id = fresh.relbase_customer_id
          notifications.showSuccess('Cliente vinculado con Relbase — datos actualizados')
        } else if (res.status === 'created') {
          this.customer.relbase_customer_id = res.relbase_customer_id
          notifications.showSuccess('Cliente creado en Relbase')
        } else if (res.status === 'already_synced') {
          notifications.showSuccess('Cliente ya está sincronizado con Relbase')
        } else {
          notifications.showError('No se pudo sincronizar: ' + (res.message || 'Error desconocido'))
        }
      })
      .catch(() => {
        notifications.showError('No se pudo sincronizar con Relbase')
      })
      .finally(() => {
        this.syncLoading = false
      })
    },
  },
  computed: {
    canUpdateMainInformation() {
      return this.$ability.can('update_main_information', 'Customer')
    },
    relbaseEnabled() {
      return this.$store.getters.getCompany.relbase_integration_enabled
    },
    fiscalFieldsReadOnly() {
      if (!this.relbaseEnabled) return false
      // Edición: ya sincronizado con Relbase
      if (this.customer.relbase_customer_id) return true
      // Creación: el lookup encontró el RUT en Relbase (fuente de verdad)
      return this.relbaseLookupFound
    }
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
}
.border-bottom {
  border-bottom: 1px solid rgba(0,0,0,0.08);
}
.text-overline {
  letter-spacing: 0.15em !important;
}
</style>